import {Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {fullManualArticle} from "../../services/ArticleService";
import {useMessageContext} from "../../contexts/MessageContext";
import {getPrompts} from "../../services/PromptsService";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const ArticlePlayground = ({setLoading, companies, type}) => {

  const [data, setData] = useState({});
  const inputFileRef = useRef(null);
  const [imageTemplate, setImageTemplate] = useState({});
  const {notificationHandler} = useMessageContext();

  const [selectedFormat, setSelectedFormat] = useState("");
  const [selectedSubformat, setSelectedSubformat] = useState("");
  const [selectedFont, setSelectedFont] = useState("Inter-Bold");

  // New state for formats and subformats from server
  const [formats, setFormats] = useState([]);
  const [subformats, setSubformats] = useState([]);
  const [loadingFormats, setLoadingFormats] = useState(false);
  const [loadingSubformats, setLoadingSubformats] = useState(false);

  const fonts = ["Inter-Bold",
    "Inter-Regular",
    "Helvetica-Bold",
    "Helvetica-Regular",
    "Lato-Bold",
    "Lato-Regular",
    "Montserrat-Bold",
    "Montserrat-Regular",
    "OpenSans-Bold",
    "OpenSans-Regular",
    "Poppins-Bold",
    "Poppins-Regular",
    "Roboto-Bold",
    "Roboto-Regular",
    "SourceSerif4-Bold",
    "SourceSerif4-Regular",
    "Ubuntu-Bold",
    "Ubuntu-Regular",
    "Manrope-Bold",
    "Manrope-Regular",
    "NotoSans-Bold",
    "NotoSans-Regular",]

  // Fetch formats when company is selected
  useEffect(() => {
    if (data.company?.id) {
      setLoadingFormats(true);
      fetchFormats(data.company.id, type === "ARTICLE" ? "ARTICLES" : "SINGLE_POST")
          .then(response => {
            setFormats(response);
            setSelectedFormat("");
            setSelectedSubformat("");
            setSubformats([]);
          })
          .catch(error => {
            console.error('Error fetching formats:', error);
            notificationHandler.error('Failed to fetch formats');
          })
          .finally(() => setLoadingFormats(false));
    } else {
      setFormats([]);
      setSelectedFormat("");
      setSelectedSubformat("");
      setSubformats([]);
    }
  }, [data.company]);

  // Fetch subformats when format is selected
  useEffect(() => {
    if (selectedFormat && data.company?.id) {
      setLoadingSubformats(true);
      fetchSubformats(data.company.id, selectedFormat)
          .then(response => {
            setSubformats(response);
            setSelectedSubformat("");
          })
          .catch(error => {
            console.error('Error fetching subformats:', error);
            notificationHandler.error('Failed to fetch subformats');
          })
          .finally(() => setLoadingSubformats(false));
    } else {
      setSubformats([]);
      setSelectedSubformat("");
    }
  }, [selectedFormat, data.company]);

  const fetchFormats = async (companyId, category) => {
    const res = await getPrompts({
      companyId: companyId,
      category: category,
      type: "FORMAT"
    });
    return res.data;
  };

  const fetchSubformats = async (companyId, format) => {
    const res = await getPrompts({formatId: format.id, companyId: companyId});
    return res.data;
  };

  const handleSubmit = () => {
    setLoading(true);

    let body = {
      ...data,
      base64Image: imageTemplate.imageBase64,
      companyId: data.company?.id,
      type: type,
      font: selectedFont,
      formatId: selectedFormat.id,
      subFormatId: selectedSubformat.id
    };

    delete body.company;

    fullManualArticle(body)
        .then(() => setData({}))
        .then(() => setImageTemplate({}))
        .then(() => setSelectedFormat(""))
        .then(() => setSelectedSubformat(""))
        .then(() => notificationHandler.success('Manual Article triggered'))
        .finally(() => setLoading(false));
  };

  const onFileChange = async (e) => {
    let body = await Promise.all([...e.target.files].map(async file => {
      return {
        name: file.name,
        imageBase64: await convertBase64(file)
      }
    }));
    setImageTemplate(body[0]);
  }

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file)
      fileReader.onload = () => {
        resolve(fileReader.result);
      }
      fileReader.onerror = (error) => {
        reject(error);
      }
    })
  }

  return (
    <Box>
      {/* Basic Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Content Settings
      </Typography>

      <TextField
          fullWidth
          required
          multiline
          rows={2}
          size="small"
          value={data.title || ""}
          margin="dense"
          id="title"
          name="title"
          label="Title"
          placeholder="Enter article title..."
          onChange={e => setData(prv => ({ ...prv, title: e.target.value }))}
          type="text"
          variant="outlined"
          sx={{ mb: 2 }}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Company *</InputLabel>
        <Select
            label="Company *"
            value={data.company || ""}
            onChange={e => setData(prv => ({ ...prv, company: e.target.value }))}
            required
        >
          {companies.map((company, i) => (
            <MenuItem key={i} value={company}>{company.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Format</InputLabel>
          <Select
              label="Format"
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              disabled={!data.company || loadingFormats}
          >
            {formats.map((format, i) => (
              <MenuItem key={i} value={format.key || format.value || format}>
                {format.label || format.name || format}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Subformat</InputLabel>
          <Select
              label="Subformat"
              value={selectedSubformat}
              onChange={(e) => setSelectedSubformat(e.target.value)}
              disabled={!selectedFormat || loadingSubformats}
          >
            {subformats.map((subformat, i) => (
              <MenuItem key={i} value={subformat.key || subformat.value || subformat}>
                {subformat.label || subformat.name || subformat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Image Upload */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Image Settings
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
        >
          Upload Image
          <input
              type="file"
              hidden
              ref={inputFileRef}
              onChange={onFileChange}
          />
        </Button>

        {imageTemplate.imageBase64 && (
          <Box
              component="img"
              sx={{ height: 80, width: 80, objectFit: "cover", borderRadius: 1, border: "1px solid #E7E9EE" }}
              src={imageTemplate.imageBase64}
          />
        )}
      </Box>

      {type === "ARTICLE" && imageTemplate.imageBase64 && (
        <>
          <Divider sx={{ my: 3 }} />

          {/* Text Styling */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
            Text Styling
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="imageTitle"
                name="imageTitle"
                label="Image Title"
                type="text"
                variant="outlined"
                value={data.imageTitle || ""}
                onChange={e => setData(prv => ({ ...prv, imageTitle: e.target.value }))}
            />
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="color"
                name="color"
                label="Color"
                type="text"
                variant="outlined"
                value={data.color || ""}
                onChange={e => setData(prv => ({ ...prv, color: e.target.value }))}
            />
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="fontSize"
                name="fontSize"
                label="Font Size"
                type="number"
                variant="outlined"
                value={data.fontSize || ""}
                onChange={e => setData(prv => ({ ...prv, fontSize: e.target.value }))}
            />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Font</InputLabel>
              <Select
                  label="Font"
                  value={selectedFont}
                  onChange={(e) => setSelectedFont(e.target.value)}
              >
                {fonts.map((font, i) => (
                  <MenuItem key={i} value={font}>{font}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Position & Size */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
            Position & Size
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="x"
                name="x"
                label="X Position"
                type="number"
                variant="outlined"
                value={data.x || ""}
                onChange={e => setData(prv => ({ ...prv, x: e.target.value }))}
            />
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="y"
                name="y"
                label="Y Position"
                type="number"
                variant="outlined"
                value={data.y || ""}
                onChange={e => setData(prv => ({ ...prv, y: e.target.value }))}
            />
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="maxWidth"
                name="maxWidth"
                label="Max Width"
                type="number"
                variant="outlined"
                value={data.maxWidth || ""}
                onChange={e => setData(prv => ({ ...prv, maxWidth: e.target.value }))}
            />
            <TextField
                size="small"
                sx={{ flex: 1 }}
                id="maxHeight"
                name="maxHeight"
                label="Max Height"
                type="number"
                variant="outlined"
                value={data.maxHeight || ""}
                onChange={e => setData(prv => ({ ...prv, maxHeight: e.target.value }))}
            />
          </Box>
        </>
      )}

      {/* Submit Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            disabled={!data.title || !data.company || !selectedFormat || !selectedSubformat}
            onClick={handleSubmit}
        >
          Generate {type === "ARTICLE" ? "Article" : "Single Post"}
        </Button>
      </Box>
    </Box>
  )
};

export default ArticlePlayground;
